/**
 * Custom Order Form
 * ------------------------------------------------------------------
 * Self-contained add-to-cart for the shared "Custom Team Order" product.
 *
 * Deliberately does NOT reuse the theme's <product-form> / ProductForm
 * component: that component reads DOCUMENT-WIDE personalization selectors
 * (#personalizeform, [data-personalization-input]) on submit, so on a PDP
 * that also has a personalization drawer it would fold that product's
 * personalization data into this form's line item. Keeping this handler
 * standalone avoids that cross-contamination.
 *
 * Reuses existing globals: routes, window.notificationEle, cart-form.
 *
 * Files: submitted as multipart FormData (JSON cannot carry files) to the
 * AJAX /cart/add, so uploads become /uploads/ URLs on the line item without
 * a full-page reload. Shopify stores each upload at a unique hashed URL
 * (no collisions) and its file storage has NO folders — so we can't nest into
 * files/customTeam/<id>/. Instead each file is renamed client-side to
 * customTeam-<sessionId>-<n>-<original> for grouping/traceability, and the
 * session id is also stored as the `_Session ID` property.
 */
if (!customElements.get("custom-order-form")) {
  const MAX_FILES = 5;
  const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB per file

  class CustomOrderForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.submitButton = this.querySelector('[type="submit"]');
      this.modal = this.closest("modal-dialog");
      this.fileInput = this.querySelector("[data-file-input]");
      this.fileList = this.querySelector("[data-file-list]");
      this.sessionId = this.makeSessionId();
    }

    connectedCallback() {
      if (!this.form) this.form = this.querySelector("form");
      this.form?.addEventListener("submit", this.onSubmit.bind(this));
      // Clear a field's error state as soon as the customer starts fixing it.
      this.form?.addEventListener("input", (e) => this.clearFieldError(e.target));
      this.form?.addEventListener("change", (e) => this.clearFieldError(e.target));
      this.fileInput?.addEventListener("change", () => this.renderFileList());
    }

    /** Short, unique-per-form-instance id used to group a submission's files. */
    makeSessionId() {
      const rand = Math.random().toString(36).slice(2, 8);
      return `${Date.now().toString(36)}-${rand}`;
    }

    sanitizeName(name) {
      return (name || "file").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(-80);
    }

    /** Render the chosen file list + flag over-limit files. Returns validity. */
    renderFileList() {
      if (!this.fileInput || !this.fileList) return true;
      const files = [...this.fileInput.files];
      this.fileList.innerHTML = "";
      let ok = true;

      files.slice(0, MAX_FILES).forEach((file) => {
        const li = document.createElement("li");
        const tooBig = file.size > MAX_FILE_BYTES;
        if (tooBig) { ok = false; li.classList.add("too-big"); }
        const mb = (file.size / 1024 / 1024).toFixed(1);
        li.innerHTML = `<span>${file.name}</span><span>${mb} MB${tooBig ? " — too large" : ""}</span>`;
        this.fileList.appendChild(li);
      });

      if (files.length > MAX_FILES) {
        ok = false;
        const li = document.createElement("li");
        li.classList.add("too-big");
        li.textContent = `Only ${MAX_FILES} files allowed — please remove ${files.length - MAX_FILES}.`;
        this.fileList.appendChild(li);
      }

      const wrap = this.fileInput.closest("[data-field]");
      wrap?.classList.toggle("has-error", !ok);
      return ok;
    }

    onSubmit(evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      if (!this.validate()) return;
      if (!this.renderFileList()) {
        this.notify(`Please fix the attached files (max ${MAX_FILES}, ${MAX_FILE_BYTES / 1024 / 1024} MB each).`, "error");
        return;
      }

      const variantInput = this.form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        this.notify("This form isn't set up yet. Please contact us.", "error");
        return;
      }

      this.submitButton.classList.add("loading");
      this.submitButton.setAttribute("aria-disabled", "true");

      let cartSectionID = "component-cart-drawer";
      if (window.globalSpace && window.globalSpace.template === "cart") {
        cartSectionID =
          document.getElementById("cart--items")?.dataset.id || "template-cart";
      }

      // Multipart FormData so any uploaded files are carried to /cart/add.
      // Named inputs (id, quantity, properties[...]) are picked up automatically;
      // the file input has no name, so we append each file explicitly.
      //
      // Per file we write TWO properties keyed by the same index N so a map can
      // reconcile them (Shopify replaces the uploaded filename with a random
      // hash, so the original name would otherwise be lost):
      //   "Design File N"   (visible) = customer's original filename as text
      //   "_Design File N"  (hidden)  = the actual file -> /uploads/ URL
      const fd = new FormData(this.form);
      fd.set("properties[_Session ID]", this.sessionId);

      const files = this.fileInput ? [...this.fileInput.files].slice(0, MAX_FILES) : [];
      files.forEach((file, i) => {
        const n = i + 1;
        // Hidden (merchant-only): the file itself becomes the /uploads/ URL.
        fd.append(`properties[_Design File ${n}]`, file, this.sanitizeName(file.name));
        // Visible (customer + order): the original filename as readable text.
        fd.set(`properties[Design File ${n}]`, file.name);
      });

      fd.append("sections", `${cartSectionID},header`);

      // IMPORTANT: routes.cart_add_url is "/cart/add" (no .js). Without an
      // Accept: application/json header Shopify replies with an HTML redirect
      // to /cart instead of JSON, response.json() throws, and the add looks
      // like it silently failed. So we must request JSON explicitly.
      // Do NOT set Content-Type — the browser sets the multipart boundary.
      fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      })
        .then(async (response) => {
          let data = null;
          try {
            data = await response.json();
          } catch (parseErr) {
            throw new Error(
              `Cart returned an unexpected response (HTTP ${response.status}). Please try again.`
            );
          }
          return { ok: response.ok, data };
        })
        .then(({ ok, data }) => {
          // Shopify returns a numeric `status` (e.g. 422) + message on add errors.
          if (!ok || data.status || data.errors) {
            const msg =
              data.description ||
              data.message ||
              data.errors ||
              "We couldn't add this to your cart. Please try again.";
            this.notify(typeof msg === "string" ? msg : JSON.stringify(msg), "error");
            console.error("Custom order add rejected:", data);
            return;
          }

          // Added successfully. Refresh/open the cart UI. Guard this separately:
          // a render error here must NOT be reported as an add failure, because
          // the item is already in the cart.
          try {
            this.modal?.hide?.();
            this.form.reset();
            if (this.fileList) this.fileList.innerHTML = "";
            this.sessionId = this.makeSessionId();

            const cartForm = document.querySelector("cart-form");
            if (cartForm && typeof cartForm._updateCart === "function") {
              if (data.sections) {
                cartForm._updateCart(data.sections, this.submitButton);
              } else {
                cartForm.getCartData?.();
              }
            } else {
              window.location.href = window.routes.cart_fetch_url;
            }
          } catch (uiErr) {
            console.error(
              "Custom order: item added but cart UI refresh failed — redirecting to cart.",
              uiErr
            );
            window.location.href = window.routes.cart_fetch_url;
          }
        })
        .catch((e) => {
          console.error("Custom order add failed:", e);
          this.notify(e?.message || "Network error. Please try again.", "error");
        })
        .finally(() => {
          this.submitButton.classList.remove("loading");
          this.submitButton.removeAttribute("aria-disabled");
        });
    }

    /**
     * Client-side validation. Fields opt in with data-required; format checks
     * via data-validate="email|phone".
     * @returns {boolean} whether the form is valid
     */
    validate() {
      let valid = true;
      let firstInvalid = null;

      this.form.querySelectorAll("[data-required]").forEach((field) => {
        let ok;
        if (field.type === "checkbox") {
          ok = field.checked;
        } else {
          const value = (field.value || "").trim();
          ok = value.length > 0;
          if (ok && field.dataset.validate === "email") {
            ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
          }
          if (ok && field.dataset.validate === "phone") {
            ok = value.replace(/[^0-9]/g, "").length >= 7;
          }
        }

        if (!ok) {
          valid = false;
          this.setFieldError(field);
          if (!firstInvalid) firstInvalid = field;
        } else {
          this.clearFieldError(field);
        }
      });

      if (firstInvalid) {
        firstInvalid.focus();
        if (!valid) {
          this.notify("Please complete the highlighted fields.", "error");
        }
      }
      return valid;
    }

    setFieldError(field) {
      field.setAttribute("aria-invalid", "true");
      field.closest("[data-field]")?.classList.add("has-error");
    }

    clearFieldError(field) {
      if (!field || typeof field.hasAttribute !== "function") return;
      field.removeAttribute("aria-invalid");
      field.closest("[data-field]")?.classList.remove("has-error");
    }

    notify(message, type = "error") {
      if (window.notificationEle?.updateNotification) {
        window.notificationEle.updateNotification("Custom Order", message, {
          type,
          timeout: 5000,
        });
      } else {
        // eslint-disable-next-line no-alert
        window.alert(message);
      }
    }
  }

  customElements.define("custom-order-form", CustomOrderForm);
}
